"use client";

import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Link,
  List,
  ListItem,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import { Field } from "../../components/Field";
import { SERVER_STATE, useForm } from "../../hooks/useForm";
import { TypeFormField } from "../content/typegen";

const generalFormFields: TypeFormField[] = [
  {
    type: "text",
    label: "Betreff",
    errorMessage: "Bitte Betreff eingeben",
    isRequired: true,
  },
  {
    type: "textarea",
    label: "Nachricht",
    errorMessage: "Bitte Nachricht eingeben",
    isRequired: true,
  },
  {
    type: "text",
    label: "Email-Adresse",
    errorMessage: "Bitte korrekte Email-Adresse eingeben",
    isRequired: true,
    validation: "email",
  },
];

export function PageComponent(): ReactNode {
  const form = useForm({
    formFieldsPerPerson: [],
    generalFormFields,
    onSubmit: async (state) => {
      const formFields = state.generalFormFields.map((formField) => ({
        label: formField.label,
        value: formField.value,
      }));
      const [apiResponse, emailResponse] = await Promise.all([
        fetch("/api/contact", {
          method: "put",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contactData: formFields }),
        }),
        fetch("/api/mail/contact", {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contactData: formFields }),
        }),
      ]);
      const [apiResponseData, emailResponseData] = await Promise.all([
        apiResponse.json(),
        emailResponse.json(),
      ]);
      if (!apiResponseData.ok) {
        throw new Error(apiResponseData.error);
      }
      if (!emailResponseData.ok) {
        throw new Error(emailResponseData.error);
      }
    },
  });
  return (
    <>
      <Heading>Kontakt</Heading>
      <Text mt={4}>
        Über dieses Formular kannst du ganz einfach Kontakt zu uns aufnehmen.
        Egal um was es geht, wir versuchen alle Fragen und Anliegen zu
        beantworten. Wir kümmern uns so schnell wie möglich um die Anfrage!
      </Text>
      {form.state.serverState === SERVER_STATE.SUCCESS ? (
        <Flex p={4} alignItems="center" rounded="md" bg="green.200" mt={4}>
          <CheckIcon />
          <Text ml={4}>
            Deine Nachricht hat uns erreicht! Wir werden uns so bald wie möglich
            darum kümmern.
          </Text>
        </Flex>
      ) : null}
      {form.state.serverState === SERVER_STATE.ERROR ? (
        <Flex p={4} alignItems="center" rounded="md" bg="red.200" mt={4}>
          <CloseIcon />
          <Text ml={4}>
            Beim Absenden des Formulars ist ein unbekannter Fehler aufgetreten.
            Wir haben das zur Kenntnis genommen und werden uns schnellstmöglich
            darum kümmern. Bitte versuchen Sie es später noch einmal!
          </Text>
        </Flex>
      ) : null}
      {form.state.serverState === SERVER_STATE.SUBMITTING ? (
        <Flex justifyContent="center" mt={4}>
          <Spinner
            size="xl"
            speed="0.5s"
            thickness="4px"
            color="blue.500"
            emptyColor="gray.200"
          />
        </Flex>
      ) : null}
      {form.state.serverState === SERVER_STATE.NOT_SUBMITTED ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.submitForm();
          }}
        >
          {form.state.generalFormFields.map((formField) => (
            <Field
              key={formField.label}
              submitCount={form.state.submitCount}
              updateValue={form.updateValue}
              {...formField}
            />
          ))}
          <Button colorScheme="blue" type="submit" mt={6}>
            absenden
          </Button>
          <FormControl
            isInvalid={form.state.submitCount > 0 && !form.state.isValid}
          >
            <FormErrorMessage>
              Das Formular enthält noch Fehler!
            </FormErrorMessage>
          </FormControl>
        </form>
      ) : null}
      <Text mt={8}>
        Ihr könnt gerne auch telefonisch oder per Email Kontakt zu uns
        aufnehmen!
      </Text>
      <List>
        <ListItem>
          Telefon:{" "}
          <Link href="tel:01782928497" color="blue.500" isExternal>
            0178 / 2928497
          </Link>
        </ListItem>
        <ListItem>
          Email:{" "}
          <Link
            href="mailto:kontakt@ski-eiselfing.de"
            color="blue.500"
            isExternal
          >
            kontakt@ski-eiselfing.de
          </Link>
        </ListItem>
      </List>
    </>
  );
}
